import { NodeViewWrapper } from "@tiptap/react";
import { useRef, useState, useEffect } from "react";
import { AlignLeft, AlignCenter, AlignRight, Maximize2, WrapText } from "lucide-react";

/**
 * Custom Tiptap NodeView for images with:
 *  - 4 corner drag handles (maintain aspect ratio)
 *  - Alignment quick buttons (left / center / right / full-width)
 *  - Wrap-Left / Wrap-Right for text-wrap around image (float)
 *  - Width stored as percentage of editor width
 */
export default function ResizableImageNode({ node, updateAttributes, selected, editor }) {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const align = node.attrs.align || "center";
  const widthPct = node.attrs.widthPct; // number 0-100 or null
  const isWrap = align === "wrap-left" || align === "wrap-right";

  // Tiptap wraps our NodeView in an outer `<div class="react-renderer node-image">`.
  // That outer element is what siblings flow around. So for text-wrap to work we must
  // apply float + width to THAT outer, not just our inner .rte-img-wrap.
  useEffect(() => {
    const outer = wrapperRef.current?.parentElement;
    if (!outer || !outer.classList.contains("react-renderer")) return;
    // Reset first so switching between modes clears prior styles
    outer.style.cssText = "";
    if (align === "wrap-left") {
      outer.style.float = "left";
      outer.style.margin = "0.25rem 1.25rem 0.75rem 0";
      outer.style.width = widthPct ? `${widthPct}%` : "auto";
    } else if (align === "wrap-right") {
      outer.style.float = "right";
      outer.style.margin = "0.25rem 0 0.75rem 1.25rem";
      outer.style.width = widthPct ? `${widthPct}%` : "auto";
    } else if (align === "full") {
      outer.style.width = "100%";
      outer.style.display = "block";
    } else {
      outer.style.width = widthPct ? `${widthPct}%` : "auto";
      outer.style.display = "block";
      if (align === "left")   { outer.style.marginRight = "auto"; outer.style.marginLeft = "0"; }
      else if (align === "right")  { outer.style.marginLeft  = "auto"; outer.style.marginRight = "0"; }
      else                          { outer.style.marginLeft  = "auto"; outer.style.marginRight = "auto"; }
    }
  }, [align, widthPct]);

  const wrapperStyle = { position: "relative", display: "block", lineHeight: 0 };

  const imgStyle = (() => {
    const s = { display: "block", maxWidth: "100%", height: "auto", borderRadius: 12, margin: 0 };
    // Outer container is sized to widthPct via the useEffect above; the img just fills it.
    s.width = "100%";
    return s;
  })();

  const setAlign = (a) => updateAttributes({ align: a });

  const startResize = (corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    const outer = wrapperRef.current?.parentElement;
    if (!img || !outer) return;
    const editorEl = editor?.view?.dom;
    const containerWidth = editorEl?.clientWidth || outer.parentElement?.clientWidth || 800;
    const startX = e.clientX;
    const startWidth = outer.getBoundingClientRect().width;
    const dir = corner === "tl" || corner === "bl" ? -1 : 1;
    setDragging(true);
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) * dir;
      let newWidth = Math.max(40, startWidth + dx);
      newWidth = Math.min(newWidth, containerWidth);
      outer.style.width = `${newWidth}px`;
      ev.preventDefault();
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const finalW = outer.getBoundingClientRect().width;
      const pct = Math.round((finalW / containerWidth) * 1000) / 10;
      updateAttributes({ widthPct: Math.min(100, Math.max(5, pct)) });
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <NodeViewWrapper
      as="div"
      className={`rte-img-wrap ${selected ? "is-selected" : ""} align-${align}`}
      data-align={align}
      style={wrapperStyle}
      ref={wrapperRef}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        title={node.attrs.title || ""}
        style={imgStyle}
        draggable={false}
        className="rte-img"
      />

      {selected && (
        <>
          {["tl", "tr", "bl", "br"].map((c) => (
            <span
              key={c}
              contentEditable={false}
              onMouseDown={startResize(c)}
              className={`rte-img-handle rte-img-handle-${c}`}
              data-testid={`rte-img-handle-${c}`}
              title="Drag to resize"
            />
          ))}

          <div
            className="rte-img-align-bar"
            contentEditable={false}
            data-testid="rte-img-align-bar"
            onMouseDown={(e) => e.preventDefault()}
          >
            <button type="button" onClick={() => setAlign("left")}       title="Align left (no wrap)"       className={align === "left"       ? "active" : ""} data-testid="rte-img-align-left"><AlignLeft className="w-3.5 h-3.5"/></button>
            <button type="button" onClick={() => setAlign("center")}     title="Align center"                className={align === "center"     ? "active" : ""} data-testid="rte-img-align-center"><AlignCenter className="w-3.5 h-3.5"/></button>
            <button type="button" onClick={() => setAlign("right")}      title="Align right (no wrap)"      className={align === "right"      ? "active" : ""} data-testid="rte-img-align-right"><AlignRight className="w-3.5 h-3.5"/></button>
            <button type="button" onClick={() => setAlign("full")}       title="Full width"                  className={align === "full"       ? "active" : ""} data-testid="rte-img-align-full"><Maximize2 className="w-3.5 h-3.5"/></button>
            <span className="rte-img-align-sep" />
            <button type="button" onClick={() => setAlign("wrap-left")}  title="Wrap text to the right (image on left)" className={align === "wrap-left"  ? "active" : ""} data-testid="rte-img-align-wrap-left"><WrapText className="w-3.5 h-3.5" style={{ transform: "scaleX(-1)" }}/></button>
            <button type="button" onClick={() => setAlign("wrap-right")} title="Wrap text to the left (image on right)"  className={align === "wrap-right" ? "active" : ""} data-testid="rte-img-align-wrap-right"><WrapText className="w-3.5 h-3.5"/></button>
          </div>

          {dragging && widthPct != null && (
            <div className="rte-img-size-badge">{Math.round(widthPct)}%</div>
          )}
        </>
      )}
      {isWrap && null}
    </NodeViewWrapper>
  );
}
